import axios from "axios";
import { t } from "i18next";

import AlertsService from "services/alerts-service";
import { putStorage } from "hooks/useStorage";

export type UniAdminDetails = {
    name: string;
    shortName?: string;
    district?: string;
    region?: string;
    city?: string;
    country?: string;
    adminContacts?: string;
};

export type CreateUniAdminPayload = {
    email: string;
    details: UniAdminDetails;
};

export type UserInfo = {
    id: string;
    email: string;
    userRole: string;
    name?: string;
    phone?: string;
    blocked?: boolean;
    isBlocked?: boolean;
    isDeleted?: boolean;
    createdAt?: string;
    universityId?: string;
    university?: {
        id: string;
        details: UniAdminDetails;
    };
};

const AdminApi = {
    // Получить список всех администраторов ВУЗов
    getUniAdmins: async (): Promise<UserInfo[]> => {
        try {
            const { data: response } = await axios.get(`/uni-admins`);
            putStorage(`uniAdmins`, response);
            return response;
        } catch (error) {
            throw AlertsService.showError("Ошибка при загрузке списка администраторов");
        }
    },

    // Создать ВУЗ с администратором
    createUniAdmin: async (payload: CreateUniAdminPayload): Promise<any> => {
        try {
            const { data: response } = await axios.post(
                `/create-uni-admin`,
                payload
            );
            AlertsService.showSuccess("ВУЗ и администратор успешно созданы");
            return response;
        } catch (error: any) {
            if (error?.response?.data?.message) {
                throw AlertsService.showError(error.response.data.message);
            }
            throw AlertsService.showError("Ошибка при создании ВУЗа");
        }
    },

    // Найти пользователя по email
    findUserByEmail: async (email: string): Promise<UserInfo | null> => {
        try {
            const { data: response } = await axios.get(`/admin/users/find-by-email`, {
                params: { email: email.trim().toLowerCase() }
            });
            return response;
        } catch (error: any) {
            if (error?.response?.status === 404) {
                return null;
            }
            throw AlertsService.showError("Ошибка при поиске пользователя");
        }
    },

    // есть запрос /..../find-by-filters - туда в query можно передать email, phone, name, universityId, universityName, universityShortName, universityDistrict, universityRegion, universityCity, universityCountry пол города или региона можно передать в query city или region    
    findUserByFilters: async (filters: { email?: string; phone?: string; name?: string; universityId?: string; universityName?: string; universityShortName?: string; universityDistrict?: string; universityRegion?: string; universityCity?: string; universityCountry?: string; city?: string; region?: string }): Promise<UserInfo[]> => {
        try {
            const { data: response } = await axios.get(`/admin/users/find-by-filters`, { params: filters });
            return response;
        } catch (error: any) {
            if (error?.response?.status === 404) {
                return null;
            }
            throw AlertsService.showError("Ошибка при поиске пользователя");
        }
    },

    // Обновить данные пользователя (админом)
    updateUser: async (userId: string, data: Partial<UserInfo>): Promise<UserInfo> => {
        try {
            const { data: response } = await axios.put(`/admin/users/${userId}`, data);
            AlertsService.showSuccess("Данные пользователя обновлены");
            return response;
        } catch (error) {
            throw AlertsService.showError("Ошибка при обновлении пользователя");
        }
    },

    // Заблокировать пользователя
    blockUser: async (userId: string): Promise<UserInfo> => {
        try {
            const { data: response } = await axios.post(`/admin/users/${userId}/block`);
            AlertsService.showSuccess("Пользователь заблокирован");
            return response;
        } catch (error) {
            throw AlertsService.showError("Ошибка при блокировке пользователя");
        }
    },

    // Разблокировать пользователя
    unblockUser: async (userId: string): Promise<UserInfo> => {
        try {
            const { data: response } = await axios.post(`/admin/users/${userId}/unblock`);
            AlertsService.showSuccess("Пользователь разблокирован");
            return response;
        } catch (error) {
            throw AlertsService.showError("Ошибка при разблокировке пользователя");
        }
    },

    // Сгенерировать новый пароль для пользователя
    generatePassword: async (userId: string): Promise<{ password: string }> => {
        try {
            const { data: response } = await axios.post(`/admin/users/${userId}/generate-password`);
            AlertsService.showSuccess("Новый пароль сгенерирован");
            return response;
        } catch (error) {
            throw AlertsService.showError("Ошибка при генерации пароля");
        }
    },

    // Удалить пользователя (soft delete)
    deleteUser: async (userId: string): Promise<UserInfo> => {
        try {
            const { data: response } = await axios.post(`/admin/users/${userId}/delete`);
            AlertsService.showSuccess("Пользователь удалён");
            return response;
        } catch (error) {
            throw AlertsService.showError("Ошибка при удалении пользователя");
        }
    },

    // Восстановить удалённого пользователя
    undeleteUser: async (userId: string): Promise<UserInfo> => {
        try {
            const { data: response } = await axios.post(`/admin/users/${userId}/undelete`);
            AlertsService.showSuccess("Пользователь восстановлен");
            return response;
        } catch (error) {
            throw AlertsService.showError("Ошибка при восстановлении пользователя");
        }
    },

    // Обновить email пользователя
    updateUserEmail: async (oldEmail: string, newEmail: string): Promise<void> => {
        try {
            await axios.post(`/update-email`, {
                old_email: oldEmail,
                new_email: newEmail
            });
            AlertsService.showSuccess("Email успешно обновлен");
        } catch (error) {
            throw AlertsService.showError("Ошибка при обновлении email");
        }
    },

    // Сбросить пароль пользователя (возвращает новый пароль)
    resetPassword: async (userId: string): Promise<{ password: string }> => {
        try {
            const { data: response } = await axios.post(`/admin/users/${userId}/reset-password`);
            AlertsService.showSuccess("Пароль успешно сброшен");
            return response;
        } catch (error) {
            throw AlertsService.showError("Ошибка при сбросе пароля");
        }
    },

    // Установить email пользователю по userId
    setUserEmail: async (userId: string, email: string): Promise<UserInfo> => {
        try {
            const { data: response } = await axios.post(`/admin/users/${userId}/set-email`, { email });
            AlertsService.showSuccess("Email успешно изменён");
            return response;
        } catch (error) {
            throw AlertsService.showError("Ошибка при изменении email");
        }
    }
};

export default AdminApi;
